/**
 * textEdit.ts
 * TextEditノードの定義
 * ユーザーがテキストを入力し、次のノードに渡すためのノード
 */
import type { NodeDefinition } from '../../types';
import { t, r } from '../../rubyParts';
import { TextEditNodeView } from '../views/TextEditNode';
import { TextEditSettings } from '../settings/TextEditSettings';
import { OFFLINE_MODE } from '@/config/offline';
import { OFFLINE_PHRASES } from '@/speech/offlineSpeech';

/**
 * TextEditノードの定義
 * - パレットに表示してユーザーがドラッグ配置できる
 * - 実行時はdata.textをctx.lastValueに保存して次のノードに渡す
 */
export const textEditDefinition: NodeDefinition<'textEdit'> = {
  label: 'Text Edit',
  showInPalette: true,      // パレットに表示
  paletteColor: 'bg-emerald-100 border-emerald-500 text-emerald-700',
  miniMapColor: '#10b981',  // エメラルド色
  defaultData: { label: 'Text Edit', text: OFFLINE_MODE ? OFFLINE_PHRASES[0].text : '' },
  view: TextEditNodeView,
  settings: TextEditSettings,
  run: async (node, ctx, log) => {
    if (OFFLINE_MODE && !OFFLINE_PHRASES.some(({ text }) => text === node.data.text)) {
      throw new Error('Text Edit: オフライン用の定型文を選択してください');
    }
    // 入力されたテキストをコンテキストに保存（次のノードで使用可能）
    ctx.lastValue = node.data.text;
    log(`[Text Edit] "${node.data.text}" を おぼえた`);
    // 0.5秒待機
    await new Promise<void>((resolve) => {
      if (ctx.signal?.aborted) { resolve(); return; }
      const timeout = window.setTimeout(resolve, 500);
      ctx.signal?.addEventListener('abort', () => { window.clearTimeout(timeout); resolve(); }, { once: true });
    });
  },
  help: {
    title: [t('Text Edit（'), r('文字', 'もじ'), t('を'), r('作', 'つく'), t('る）')],
    description: OFFLINE_MODE ? [t('事前作成した10個の文章から選びます。Say Textにつなぐと同梱された音声を再生します。')] : [
      t('「ことば」を'),
      r('自分', 'じぶん'),
      t('で'),
      r('書', 'か'),
      t('いて'),
      r('作', 'つく'),
      t('るカードです。'),
      r('作', 'つく'),
      t('った「ことば」は、つぎのカードに'),
      r('渡', 'わた'),
      t('されます。'),
    ],
  },
};
